import { useQueryClient } from '@tanstack/react-query';
import { PaymentMethods, SubscriptionPlan } from '@/account/Interfaces';
import { useGetPaymentType } from '@/account/payments/paymentsUpdateModal/PaymentsComponents/constants';
import {
  useProcessCard,
  useReactivateAccountTimeLeft,
  useProcessPayPal,
  useProcessBraintree,
} from '@/account/payments/useSubscriptionUpgrade';
import { useSnackbar } from '@/alerts/index';
import { useSession } from '@/auth/Session';
import { useAppConfigContext } from '@/context/AppConfig';
import { useRedirect } from '@/utils/hooks/useRedirect';
import { DateUtil } from '@/utils/index';

const dateUtil = new DateUtil();

export const useProcessPayment = () => {
  const { logError } = useAppConfigContext();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { redirect } = useRedirect();

  const queryClient = useQueryClient();
  const reactivationProcessTimeLeft = useReactivateAccountTimeLeft();
  const reactivationProcessCardQuery = useProcessCard();
  const reactivationProcessPayPalQuery = useProcessPayPal();
  const reactivationProcessBraintreeQuery = useProcessBraintree();

  const {
    session: { isAuthenticated },
    useAccount,
    setSession,
  } = useSession();

  const { refetch: refetchAccount } = useAccount(isAuthenticated);

  function processPayment(
    expirationDate: string | undefined,
    activePayment: PaymentMethods | undefined,
    showProcessingModal: boolean,
    selectedPlan: SubscriptionPlan,
    setCTADisabled: (disabled: boolean) => void
  ): boolean {
    setCTADisabled(true);
    const hasTimeLeft =
      (dateUtil.getDaysDiffFromNow(expirationDate ?? '') ?? 0) > 0;

    if (hasTimeLeft) {
      reactivationProcessTimeLeft.mutate('true', {
        onSuccess() {
          enqueueSnackbar('Account reactivated successfully.', {
            variant: 'success',
          });
          queryClient.invalidateQueries({ queryKey: ['account'] });
          redirect('/dashboard');
        },
        onError(error) {
          logError('Reactivating account with time left error', error as Error);
          enqueueSnackbar(
            'Something went wrong, please try again or contact customer service',
            { variant: 'error' }
          );
          setCTADisabled(false);
          showProcessingModal = false;
        },
      });

      return showProcessingModal;
    }

    interface MutationFunctions {
      paypal: typeof reactivationProcessPayPalQuery;
      braintree: typeof reactivationProcessBraintreeQuery;
      card: typeof reactivationProcessCardQuery;
    }

    const mutationFunctions: MutationFunctions = {
      paypal: reactivationProcessPayPalQuery,
      braintree: reactivationProcessBraintreeQuery,
      card: reactivationProcessCardQuery,
    };

    if (!activePayment) return false;

    const paymentType = useGetPaymentType(activePayment);

    const mutationFunction =
      mutationFunctions[paymentType] || reactivationProcessCardQuery;

    const mutationParams = {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      plan: { unique_key: selectedPlan?.unique_key },
      ...((paymentType === 'paypal' && {
        paypalId: activePayment.id,
      }) as { paypalId: number }),
      ...((paymentType === 'card' && { cardId: activePayment.id }) as {
        cardId: number;
      }),
      ...((paymentType === 'braintree' && {
        braintreeId: activePayment.id,
      }) as { braintreeId: number }),
      source_of_payment: 'manual_reactivation_attempt',
    };

    mutationFunction.mutate(mutationParams, {
      async onSuccess() {
        closeSnackbar();
        enqueueSnackbar('Account reactivated successfully.', {
          variant: 'success',
        });
        const { data } = await refetchAccount();
        setSession({
          account: data,
        });
        redirect('/dashboard');
      },
      async onError(error: Error) {
        logError(
          `Reactivation error with ${paymentType} payment method.`,
          error as Error
        );
        setCTADisabled(false);
        showProcessingModal = false;

        const cause = error?.cause as unknown as string[];
        const stringifiedErrors = cause?.join(', ');

        if (stringifiedErrors === 'Your account is already reactivated') {
          closeSnackbar();
          enqueueSnackbar(`${stringifiedErrors}.`, { variant: 'success' });
          const { data } = await refetchAccount();
          setSession({
            account: data,
          });
          redirect('/dashboard');
          return;
        }

        enqueueSnackbar(
          'Your payment failed. Please try again or try another payment method.',
          { variant: 'error' }
        );
      },
    });

    return showProcessingModal;
  }

  return { processPayment };
};
